import React, { useEffect, useState } from 'react';
import UQLoader from '../loader';
import { iconCache, loadingPromises } from './icon-preloader';

export interface IconProps extends React.HTMLAttributes<HTMLDivElement> {
  icon: string;
}

const DEFAULT_ICON = 'x';

const UQIcon: React.FC<IconProps> = ({ icon, ...rest }) => {
  const [svgContent, setSvgContent] = useState<string | null>(null);
  const [currentIcon, setCurrentIcon] = useState(icon);
  const [isLoading, setIsLoading] = useState(false);

  const icons = import.meta.glob('/src/assets/icons/*.svg', { query: '?raw', import: 'default' });

  const fetchSvg = async (iconName: string) => {
    try {
      const iconPath = `/src/assets/icons/${iconName}.svg`;

      if (iconCache.has(iconName)) {
        setSvgContent(iconCache.get(iconName)!);
        setIsLoading(false);
        return;
      }

      if (loadingPromises.has(iconName)) {
        const svg = await loadingPromises.get(iconName)!;
        setSvgContent(svg);
        setIsLoading(false);
        return;
      }

      if (!icons[iconPath]) {
        if (iconName !== DEFAULT_ICON) {
          setCurrentIcon(DEFAULT_ICON);
        } else {
          console.error(`UQIcon: Icon "${iconName}" not found and no default icon available.`);
          setSvgContent(null);
          setIsLoading(false);
        }
        return;
      }

      setIsLoading(true);
      
      const loadingPromise = icons[iconPath]().then((svg: unknown) => {
        const svgString = svg as string;
        iconCache.set(iconName, svgString);
        loadingPromises.delete(iconName);
        return svgString;
      });

      loadingPromises.set(iconName, loadingPromise);
      const svg = await loadingPromise;
      setSvgContent(svg);
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
      if (iconName !== DEFAULT_ICON) {
        setCurrentIcon(DEFAULT_ICON);
      } else {
        console.error(`UQIcon: Error fetching icon "${iconName}".`, error);
        setSvgContent(null);
      }
    }
  };

  useEffect(() => {
    setCurrentIcon(icon);
  }, [icon]);

  useEffect(() => {
    fetchSvg(currentIcon);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentIcon]);

  const iconClass = `icon ${currentIcon}`.trim();

  if (isLoading) {
    return (
      <div className={iconClass} {...rest}>
        <span className="icon-placeholder">
          <UQLoader size="s" />
        </span>
      </div>
    );
  }

  if (!svgContent) {
    return (
      <div className={iconClass} {...rest}>
        <span className="icon-placeholder">?</span>
      </div>
    );
  }

  return (
    <div
      className={iconClass}
      dangerouslySetInnerHTML={{ __html: svgContent }}
      {...rest}
    ></div>
  );
};

export default UQIcon;
